import type { ReactNode } from 'react'
import type { Choice } from './employeeFieldOptions'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'

/**
 * The field wrappers the employee forms share.
 *
 * <p>Pulled out because the wizard and the four profile edit dialogs ask for the same things,
 * and a "Nationality" that is a free-text box in one place and a two-letter code in another is
 * how the same person ends up recorded as "UAE" and "AE".
 */

/**
 * A labelled control, with the server's message for it underneath when there is one.
 *
 * <p>The error replaces the hint rather than sitting beside it: two lines of small print under
 * one box, one grey and one red, get read as one, and the grey one is usually first.
 */
export function Field({
  id, label, hint, required, error, children,
}: {
  id: string
  label: string
  hint?: string
  required?: boolean
  error?: string
  children: ReactNode
}) {
  return (
    <div
      className={error
        ? 'space-y-1 [&_input]:border-[var(--color-danger)] [&_[role=combobox]]:border-[var(--color-danger)]'
        : 'space-y-1'}
    >
      <Label htmlFor={id}>
        {label}
        {required && <span className="ms-1 text-[var(--color-danger)]" aria-hidden="true">*</span>}
      </Label>
      {children}
      {error
        ? <p id={`${id}-error`} role="alert" className="text-xs text-[var(--color-danger)]">{error}</p>
        : hint && <p className="text-xs text-[var(--color-muted)]">{hint}</p>}
    </div>
  )
}

export function TextField({
  id, label, value, onChange, hint, required, type = 'text', placeholder, error,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  hint?: string
  required?: boolean
  type?: string
  placeholder?: string
  error?: string
}) {
  return (
    <Field id={id} label={label} hint={hint} required={required} error={error}>
      <Input
        id={id}
        type={type}
        value={value}
        required={required}
        placeholder={placeholder}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        onChange={(event) => onChange(event.target.value)}
      />
    </Field>
  )
}

export interface NamedOption {
  id: string
  name: string
}

export function OptionField({
  id, label, value, options, onChange, hint, required, emptyLabel = 'Not set', error,
}: {
  id: string
  label: string
  value: string
  options: NamedOption[] | undefined
  onChange: (value: string) => void
  hint?: string
  required?: boolean
  emptyLabel?: string
  error?: string
}) {
  return (
    <Field id={id} label={label} hint={hint} required={required} error={error}>
      <Select
        id={id}
        value={value}
        required={required}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">{emptyLabel}</option>
        {options?.map((option) => (
          <option key={option.id} value={option.id}>{option.name}</option>
        ))}
      </Select>
    </Field>
  )
}

export function ChoiceField({
  id, label, value, choices, onChange, hint, required, emptyLabel = 'Not set', error,
}: {
  id: string
  label: string
  value: string
  choices: readonly Choice[]
  onChange: (value: string) => void
  hint?: string
  required?: boolean
  emptyLabel?: string
  error?: string
}) {
  return (
    <Field id={id} label={label} hint={hint} required={required} error={error}>
      <Select
        id={id}
        value={value}
        required={required}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">{emptyLabel}</option>
        {choices.map((choice) => (
          <option key={choice.value} value={choice.value}>{choice.label}</option>
        ))}
      </Select>
    </Field>
  )
}
